import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  consumeForceShow,
  isSpotlightsDisabled,
  ensureSeenLoaded,
  isSeenLoaded,
  isSeen,
  markSeen,
  subscribeSeen,
} from '../featureSpotlightRegistry';
import { getBrand } from '../utils/brand';

const STORAGE_PREFIX = 'feature_seen_v1__';
const AUTO_DISMISS_MS = 12000;

function storageKey(key, userId) {
  return userId ? `${STORAGE_PREFIX}${key}__${userId}` : `${STORAGE_PREFIX}${key}`;
}

export function useFeatureSpotlight(key) {
  const { user, loading } = useAuth() || {};
  // Los spotlights son NUESTRO canal de feedback: el texto termina pidiendo "decime si
  // rompe algo o se puede mejorar", dirigido a Andy. En la instancia de un cliente no
  // tienen destinatario, y además se disparan TODOS juntos la primera vez que entra
  // alguien (hoy son 64), que es justo la experiencia opuesta a la que buscan.
  const soloNuestra = getBrand()?.esAyudaTecno !== false;
  const userId = user?.id || null;
  const anchorRef = useRef(null);
  const [shouldShow, setShouldShow] = useState(false);
  const [tick, setTick] = useState(0);

  // Re-evaluate whenever the shared seen-store changes (server sync resolves,
  // a dismissal happens, or the user switches accounts).
  useEffect(() => subscribeSeen(() => setTick((n) => n + 1)), []);

  useEffect(() => {
    if (!key || !soloNuestra) return;
    // CRITICAL: wait until auth has hydrated before deciding anything.
    // AuthContext starts with user=null/loading=true and resolves /me async,
    // so on every hard refresh there's a window where userId is null. Deciding
    // during that window reads the wrong key and re-fires already-dismissed
    // spotlights. Gate on a resolved userId.
    if (loading || !userId) {
      setShouldShow(false);
      return;
    }
    // Kick off the one-time server load of this user's seen keys (deduped).
    ensureSeenLoaded(userId);
    try {
      // "Try it" from the What's-new popover stashes the key in sessionStorage.
      // Consume it here — re-fire the spotlight even if previously dismissed.
      // This is a device-local "show me again"; we intentionally don't clear the
      // server seen flag (re-dismissing just re-confirms it). Bypasses the pause.
      if (consumeForceShow(key)) {
        try { localStorage.removeItem(storageKey(key, userId)); } catch { /* ignore */ }
        setShouldShow(true);
        return;
      }
      // Respect the per-user global pause toggle.
      if (isSpotlightsDisabled(userId)) {
        setShouldShow(false);
        return;
      }
      // Wait for the server merge before deciding, so we never flash a spotlight
      // the user already dismissed on another device. Self-correcting: when the
      // store later loads/changes, the subscribe-driven tick re-runs this.
      if (!isSeenLoaded()) {
        setShouldShow(false);
        return;
      }
      setShouldShow(!isSeen(key, userId));
    } catch {
      setShouldShow(false);
    }
  }, [key, userId, loading, tick]);

  const dismiss = useCallback(() => {
    setShouldShow(false);
    // Persists to localStorage cache + server (source of truth) + notifies subs.
    if (userId) markSeen(key, userId);
  }, [key, userId]);

  return { shouldShow: soloNuestra && shouldShow, dismiss, anchorRef };
}

export default function FeatureSpotlight({
  anchorRef,
  show,
  onDismiss,
  title,
  body,
  placement = 'bottom', // 'bottom' | 'top' | 'left' | 'right'
  feedback, // undefined = auto (hidden on the client portal, shown for internal); true/false to force
}) {
  const { t } = useTranslation();
  const [rect, setRect] = useState(null);

  // The "¿Funciona bien? Avisame si rompe" prompt is aimed at internal agents/Andy.
  // On the client portal it reads oddly, so the spotlight still fires (ring + callout
  // announcing the feature) but drops the feedback line. Auto-detected from the route
  // so every portal spotlight inherits it with zero extra props — pass feedback={true|false}
  // to override.
  const onPortal = typeof window !== 'undefined' && window.location.pathname.startsWith('/portal');
  const showFeedback = feedback == null ? !onPortal : feedback;

  useLayoutEffect(() => {
    if (!show || !anchorRef?.current) return undefined;
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const interval = setInterval(measure, 250); // re-measure as layout settles
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      clearInterval(interval);
    };
  }, [show, anchorRef]);

  useEffect(() => {
    if (!show) return undefined;
    const timer = setTimeout(() => onDismiss && onDismiss(), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [show, onDismiss]);

  if (!show || !rect) return null;

  const PAD = 6;
  const ringStyle = {
    position: 'fixed',
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
    borderRadius: 10,
    pointerEvents: 'none',
    boxShadow: '0 0 0 3px var(--c-brand), 0 0 0 6px rgba(59,130,246,0.25)',
    animation: 'feature-spotlight-pulse 1.6s ease-in-out infinite',
    zIndex: 1200,
  };

  const CALLOUT_W = 280;
  let calloutTop = rect.top + rect.height + PAD + 10;
  let calloutLeft = rect.left + rect.width / 2 - CALLOUT_W / 2;
  if (placement === 'top') calloutTop = rect.top - PAD - 10 - 110;
  if (placement === 'left') {
    calloutTop = rect.top + rect.height / 2 - 55;
    calloutLeft = rect.left - CALLOUT_W - 14;
  }
  if (placement === 'right') {
    calloutTop = rect.top + rect.height / 2 - 55;
    calloutLeft = rect.left + rect.width + 14;
  }
  calloutLeft = Math.max(8, Math.min(window.innerWidth - CALLOUT_W - 8, calloutLeft));
  calloutTop = Math.max(8, Math.min(window.innerHeight - 130, calloutTop));

  const calloutStyle = {
    position: 'fixed',
    top: calloutTop,
    left: calloutLeft,
    width: CALLOUT_W,
    background: 'var(--c-surface)',
    color: 'var(--c-text)',
    border: '1px solid var(--c-border)',
    borderLeft: '3px solid var(--c-brand)',
    borderRadius: 8,
    padding: '10px 12px 12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    zIndex: 1201,
    fontSize: 13,
    lineHeight: 1.4,
  };

  const node = (
    <>
      <div style={ringStyle} />
      <div style={calloutStyle} role="dialog" aria-live="polite">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--c-brand)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
              {t('spotlight.newLabel')}
            </div>
            {title && <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>}
            {body && <div style={{ color: 'var(--c-text-muted)', marginBottom: showFeedback ? 8 : 0 }}>{body}</div>}
            {showFeedback && (
              <div style={{ fontSize: 12, color: 'var(--c-text-muted)', fontStyle: 'italic' }}>
                {t('spotlight.feedbackPrompt')}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('common.close')}
            className="btn btn-ghost btn-icon"
            style={{ padding: 2, marginTop: -2 }}
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(node, document.body);
}
